import { Box, Button, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Granularity, LabelType } from '../types';
import { labelColors } from '../theme';

interface LabelFormProps {
  selectedFragment: { text: string; index: number } | null;
  labelType: LabelType;
  reason: string;
  setReason: (value: string) => void;
  isEditing: boolean;
  handleSubmitLabel: () => void;
  handleSubmitWithType: (type: LabelType) => void;
  handleDeleteLabel: () => void;
  handleCancelEdit: () => void;
  granularity?: Granularity;
}

const LabelForm: React.FC<LabelFormProps> = ({
  selectedFragment,
  labelType,
  reason,
  setReason,
  isEditing,
  handleSubmitLabel,
  handleSubmitWithType,
  handleDeleteLabel,
  handleCancelEdit,
  granularity = 'sentence',
}) => {
  const [reasonError, setReasonError] = useState<string>('');

  // Check if reason is required (for uncertain type) and validate
  useEffect(() => {
    if (labelType === 'uncertain' && reason.trim() === '') {
      setReasonError('Reason is required for uncertain labels');
    } else {
      setReasonError('');
    }
  }, [labelType, reason]);

  const isUncertainDisabled = !selectedFragment || reason.trim() === '';
  const isSubmitDisabled = !selectedFragment;

  const onSubmitWithType = (type: LabelType) => {
    if (type === 'uncertain' && reason.trim() === '') {
      setReasonError('Reason is required for uncertain labels');
      return;
    }

    handleSubmitWithType(type);
  };

  return (
    <Box sx={{ pt: 1, mt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Typography variant="h6" gutterBottom>
        Create Label{' '}
        {!selectedFragment && (
          <Typography component="span" color="text.secondary" variant="body2">
            (Select a {granularity === 'essay' ? 'transcription' : granularity} first)
          </Typography>
        )}
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={2}
        value={reason}
        onChange={e => setReason(e.target.value)}
        variant="outlined"
        label="Reason for selection (required for Uncertain)"
        error={!!reasonError}
        helperText={reasonError}
        sx={{ mb: 1 }}
        disabled={isSubmitDisabled}
      />

      {!isEditing ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => onSubmitWithType('positive')}
            disabled={isSubmitDisabled}
            sx={{
              backgroundColor: labelColors.positive.main,
              '&:hover': { backgroundColor: labelColors.positive.hover },
              border: `1px solid ${labelColors.positive.border}`,
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Label Positive
          </Button>
          <Button
            variant="contained"
            onClick={() => onSubmitWithType('negative')}
            disabled={isSubmitDisabled}
            sx={{
              backgroundColor: labelColors.negative.main,
              '&:hover': { backgroundColor: labelColors.negative.hover },
              border: `1px solid ${labelColors.negative.border}`,
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Label Negative
          </Button>
          <Button
            variant="contained"
            onClick={() => onSubmitWithType('both')}
            disabled={isSubmitDisabled}
            sx={{
              backgroundColor: labelColors.both.main,
              '&:hover': { backgroundColor: labelColors.both.hover },
              border: `1px solid ${labelColors.both.border}`,
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Label Both
          </Button>
          <Button
            variant="contained"
            onClick={() => onSubmitWithType('uncertain')}
            disabled={isUncertainDisabled}
            sx={{
              backgroundColor: labelColors.uncertain.main,
              '&:hover': { backgroundColor: labelColors.uncertain.hover },
              border: `1px solid ${labelColors.uncertain.border}`,
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Label Uncertain
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleSubmitLabel}
            disabled={labelType === 'uncertain' && reason.trim() === ''}
            sx={{
              backgroundColor: labelColors[labelType].main,
              '&:hover': {
                backgroundColor: labelColors[labelType].hover,
              },
              border: `1px solid ${labelColors[labelType].border}`,
              color: labelType === 'uncertain' ? 'rgba(0, 0, 0, 0.87)' : undefined,
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Update Label
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDeleteLabel}
            sx={{
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Delete Label
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancelEdit}
            sx={{
              fontSize: '0.8rem',
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            Cancel Edit
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LabelForm;
